/**
 * Voice-follow matching — pure, DOM-free, testable.
 *
 * Given the full script as a token list and the presenter's recently-spoken
 * words, find where in the script (at or ahead of the current position) the
 * speaker is now reading. Returns the script token index of the current word,
 * or -1 when there's no confident match (ad-lib / guest talking / off-script) —
 * in which case the caller should HOLD rather than move.
 */
import { flattenBlocks } from './parser.js';
/**
 * Blocks the presenter actually reads aloud. Stage directions ([SMILE], [PAUSE],
 * [CAMERA 2]) and markers are NOT spoken, so voice-follow must treat them as
 * transparent — otherwise the scroll stalls on an instruction, waiting for words
 * that are never spoken. Reading the paragraph *after* a direction then advances
 * the position and glides straight through it.
 */
export function isSpokenBlock(block) {
    return block.type === 'paragraph' || block.type === 'question';
}
/**
 * Flatten a project into a word-token stream with the block id each token belongs to.
 * Only spoken blocks contribute tokens (see {@link isSpokenBlock}), so directions
 * and markers never anchor the voice-follow position.
 */
export function scriptTokenStream(project) {
    const tokens = [];
    const blockIds = [];
    for (const block of flattenBlocks(project)) {
        if (!isSpokenBlock(block))
            continue;
        for (const token of tokenize(block.text)) {
            tokens.push(token);
            blockIds.push(block.id);
        }
    }
    return { tokens, blockIds };
}
/** Normalise text to comparable word tokens (lowercase, punctuation stripped). */
export function tokenize(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s']/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
}
/**
 * Best script token index the speaker is at, or -1 if not confident.
 * Aligns the last `span` spoken tokens to end at each candidate script index in
 * [fromIdx-back, fromIdx+window], scoring exact word hits (with a little slack),
 * and returns the highest-scoring position — biased slightly forward.
 */
export function matchSpokenToScript(scriptTokens, spoken, fromIdx, opts = {}) {
    const window = opts.window ?? 40;
    const span = opts.span ?? 6;
    const back = opts.back ?? 4;
    if (spoken.length === 0 || scriptTokens.length === 0)
        return -1;
    const recent = spoken.slice(-span);
    // Require at least 2 aligned words so a single repeated word can't drive a jump.
    const minScore = opts.minScore ?? Math.max(2, Math.min(3, recent.length));
    const start = Math.max(0, fromIdx - back);
    const end = Math.min(scriptTokens.length, fromIdx + window);
    let bestIdx = -1;
    let bestScore = 0;
    for (let i = start; i < end; i += 1) {
        let score = 0;
        for (let k = 0; k < recent.length; k += 1) {
            const si = i - k;
            const wi = recent.length - 1 - k;
            if (si < 0)
                break;
            if (scriptTokens[si] === recent[wi])
                score += 1;
        }
        // Strictly-greater only: keeps the FIRST (nearest, since we scan from just
        // behind the current position) occurrence of the best score — so on a weak
        // match we stay near where we are rather than leaping to a far repeat.
        if (score > bestScore) {
            bestScore = score;
            bestIdx = i;
        }
    }
    return bestScore >= minScore ? bestIdx : -1;
}
//# sourceMappingURL=voicematch.js.map